/// <reference lib="dom" />
/**
 * Left panel — orchestrator that delegates to per-tab render functions.
 *
 * Each sub-panel exports a render function that takes its dependencies as arguments and returns a
 * TemplateResult — the same pattern as imports-panel, signals-panel, etc. Only this orchestrator
 * uses mount/render/unmount because it owns the DOM root and error boundary.
 */

import { html, render as litRender, nothing } from "lit-html";
import type { TemplateResult } from "lit-html";
import { leftPanel, updateSession } from "../store";
import { effect, effectScope } from "../reactivity";
import { createPanelScheduler } from "./panel-scheduler";
import type { PanelScheduler } from "./panel-scheduler";
import { activeTab } from "../workspace/workspace";
import { view } from "../view";
import { mutateUpdateFrontmatter, transact } from "../tabs/transact";
import type { GitDiffState, JsonValue } from "../types";
import type { JxHeadEntry, JxMutableNode } from "@jxsuite/schema/types";

import { renderLayersTemplate } from "./layers-panel";
import { renderStylebookLayersTemplate } from "./stylebook-layers-panel";
import { renderElementsTemplate } from "./elements-panel";
import { selectStylebookTag, stylebookMeta } from "./stylebook-panel";
import type { renderImportsTemplate } from "./imports-panel";
import type { renderSignalsTemplate } from "./signals-panel";
import type { renderDataExplorerTemplate } from "./data-explorer";
import type { renderHeadTemplate } from "./head-panel";
import type { renderGitPanel } from "./git-panel";
import type { EffectScope } from "@vue/reactivity";

interface LeftPanelCtx {
  getCanvasMode: () => string;
  setCanvasMode: (mode: string) => void;
  // Renderers injected from studio.ts (dependency inversion avoids circular imports);
  // Typed against their implementations so call sites stay checked.
  renderImportsTemplate: typeof renderImportsTemplate;
  renderFilesTemplate: () => TemplateResult;
  renderSignalsTemplate: typeof renderSignalsTemplate;
  renderDataExplorerTemplate: typeof renderDataExplorerTemplate;
  renderHeadTemplate: typeof renderHeadTemplate;
  renderGitPanel: typeof renderGitPanel;
  renderCanvas: () => void;
  defCategory: (def: unknown) => string;
  defBadgeLabel: (def: unknown) => string;
  navigateToComponent: (path: string) => void;
  webdata: Record<string, unknown>;
  defaultDef: (tag: string) => Record<string, unknown>;
  registerLayersDnD: () => void;
  registerElementsDnD: () => void;
  registerComponentsDnD: () => void;
  setupTreeKeyboard: (tree: HTMLElement) => void;
  registerFileTreeDnD: (ctx: { renderLeftPanel: () => void }) => void;
  setGitDiffState: (state: GitDiffState | null) => void;
  cloneRepository?: () => void;
}

let _ctx: LeftPanelCtx | null = null;

let _scope: EffectScope | null = null;

let _scheduler: PanelScheduler | null = null;

/**
 * Mount the left panel orchestrator.
 *
 * @param {LeftPanelCtx} ctx
 */
export function mount(ctx: LeftPanelCtx) {
  _ctx = ctx;
  _scheduler = createPanelScheduler({ render: _doRender, root: leftPanel });
  _scheduler.bindFocus();
  _scope = effectScope();
  _scope.run(() => {
    effect(() => {
      const tab = activeTab.value;
      if (tab) {
        // Track properties the left panel reads
        void tab.doc.document;
        void tab.doc.mode;
        void tab.session.selection;
        void tab.session.ui.settingsTab;
        void tab.session.ui.gitStatus;
        void tab.session.ui.gitLoading;
        void tab.session.ui.gitError;
      }
      render();
    });
  });
}

export function unmount() {
  _scope?.stop();
  _scope = null;
  _ctx = null;
  _scheduler?.unbind();
  _scheduler = null;
}

/**
 * Request a render. Coalesced and deferred while a text input in the panel is focused (so explicit
 * callers — renderOnly("leftPanel"), tab switches, etc. — can never clobber a field mid-edit).
 */
export function render() {
  _scheduler?.schedule();
}

/** Actual DOM paint, invoked by the scheduler. Includes a Lit-marker-corruption recovery retry. */
function _doRender() {
  if (!_ctx) {
    return;
  }
  try {
    _render();
  } catch (error) {
    console.error("left-panel render error:", error);
    try {
      leftPanel.textContent = "";
      // @ts-expect-error — clear Lit's internal state to recover from marker corruption
      delete leftPanel["_$litPart$"];
      _render();
    } catch (retryError) {
      console.error("left-panel retry failed:", retryError);
    }
  }

  if (view.leftTab === "layers") {
    const sel = leftPanel.querySelector(".layer-row.selected");
    if (sel) {
      sel.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }
}

/** Overlay content-mode frontmatter title/$head onto the document for the head panel. */
function buildHeadDoc(doc: JxMutableNode, fm: Record<string, unknown>): JxMutableNode {
  const title = fm.title as string | undefined;
  const $head = fm.$head as JxHeadEntry[] | undefined;
  return {
    ...doc,
    ...(title !== undefined ? { title } : {}),
    ...($head !== undefined ? { $head } : {}),
  };
}

function _render() {
  const ctx = _ctx as LeftPanelCtx;
  const tab = view.leftTab;

  // ── Project-level panels: render based on projectState, independent of active tab ──

  if (tab === "files") {
    litRender(html`<div class="panel-body">${ctx.renderFilesTemplate()}</div>`, leftPanel);
    const tree = leftPanel.querySelector(".file-tree") as HTMLElement | null;
    if (tree) {
      ctx.setupTreeKeyboard(tree);
    }
    ctx.registerFileTreeDnD({ renderLeftPanel: render });
    return;
  }

  if (tab === "git") {
    const aTab = activeTab.value;
    const ui: Parameters<LeftPanelCtx["renderGitPanel"]>[0]["ui"] = aTab ? aTab.session.ui : {};
    litRender(html`<div class="panel-body">${ctx.renderGitPanel({ ui }, ctx)}</div>`, leftPanel);
    return;
  }

  if (tab === "blocks") {
    const content = renderElementsTemplate({
      defaultDef: ctx.defaultDef,
      rerender: render,
      webdata: ctx.webdata,
    } as Parameters<typeof renderElementsTemplate>[0]);
    litRender(html`<div class="panel-body">${content}</div>`, leftPanel);
    ctx.registerElementsDnD();
    ctx.registerComponentsDnD();
    return;
  }

  // ── Document-level panels: require an active tab ──

  const aTab = activeTab.value;
  if (!aTab) {
    litRender(html`<div class="panel-body"></div>`, leftPanel);
    return;
  }

  const S = /**
   * @type {{
   *   ui: unknown;
   *   document: JxMutableNode;
   *   mode: string;
   *   selection: JxPath | null;
   *   canvas: { scope?: object } | null;
   *   content?: { frontmatter?: Record<string, unknown> };
   *   documentPath?: string;
   * }}
   */ {
    canvas: aTab.session.canvas,
    content: aTab.doc.content,
    document: aTab.doc.document,
    documentPath: aTab.documentPath,
    mode: aTab.doc.mode,
    selection: aTab.session.selection,
    ui: aTab.session.ui,
  };

  /** @type {TemplateResult | typeof nothing} */
  let content;
  if (tab === "layers") {
    content =
      ctx.getCanvasMode() === "stylebook"
        ? renderStylebookLayersTemplate({
            selectStylebookTag,
            stylebookMeta,
          } as Parameters<typeof renderStylebookLayersTemplate>[0])
        : renderLayersTemplate({
            navigateToComponent: ctx.navigateToComponent,
            rerender: render,
          });
  } else if (tab === "imports") {
    content = ctx.renderImportsTemplate({
      applyMutation: (fn: (doc: JxMutableNode) => void) => {
        transact(activeTab.value, fn);
      },
      documentElements: S.document.$elements || [],
      documentPath: S.documentPath,
      renderLeftPanel: render,
    });
  } else if (tab === "state") {
    content = ctx.renderSignalsTemplate(S, {
      renderCanvas: ctx.renderCanvas,
      renderLeftPanel: render,
      updateSession,
    });
  } else if (tab === "data") {
    content = ctx.renderDataExplorerTemplate(S.document.state ?? {}, S.canvas?.scope ?? null, {
      defBadgeLabel: ctx.defBadgeLabel,
      defCategory: ctx.defCategory,
      renderCanvas: ctx.renderCanvas,
      renderLeftPanel: render,
    });
  } else if (tab === "head") {
    const isContent = S.mode === "content";
    const fm = S.content?.frontmatter ?? {};
    const headDoc = isContent ? buildHeadDoc(S.document, fm) : S.document;
    content = ctx.renderHeadTemplate({
      applyMutation: isContent
        ? (fn: (doc: JxMutableNode) => void) => {
            const tabNow = activeTab.value!;
            const fmNow = (tabNow.doc.content?.frontmatter ?? {}) as Record<string, unknown>;
            const fmHead = fmNow.$head as JxHeadEntry[] | undefined;
            const tmp: JxMutableNode = {
              ...(typeof fmNow.title === "string" ? { title: fmNow.title } : {}),
              ...(fmHead ? { $head: [...fmHead] } : {}),
            };
            fn(tmp);
            if (tmp.title !== fmNow.title) {
              mutateUpdateFrontmatter(tabNow, "title", tmp.title as JsonValue);
            }
            const newHead = tmp.$head && tmp.$head.length > 0 ? tmp.$head : undefined;
            // JxHeadEntry[] is JSON document content by construction.
            mutateUpdateFrontmatter(tabNow, "$head", newHead as JsonValue);
            render();
          }
        : (fn: (doc: JxMutableNode) => void) => {
            transact(activeTab.value, fn);
          },
      document: headDoc,
      renderLeftPanel: render,
    });
  } else {
    content = nothing;
  }

  litRender(html`<div class="panel-body">${content}</div>`, leftPanel);

  // Post-render side effects
  if (tab === "layers" && ctx.getCanvasMode() !== "stylebook") {
    ctx.registerLayersDnD();
  }
}
